import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './CircularSelect.module.scss';
import clsx from 'clsx';
import { plusPurpleCircle, PURPLE_CROSS, PURPLE_DROPDOWN_CARET } from '../../../../assets/hostedassets';
import LoadingSpinner from '../../../_custom/LoadingSpinner/LoadingSpinner';

interface CircularSelectProps {
    option: string[];
    onSelect: (value: string) => Promise<boolean>;
    selectedOption?: string;
    placeholder?: string;
    inputStyles?: React.CSSProperties;
    /** Extra class merged onto the select box (button or input), alongside the default selectBox/selectedBox/finalized classes. */
    selectClassName?: string;
    disabled?: boolean;
    isFinalized?: boolean;
    isSelectingDocType?: boolean;
    /** When false, hides the "Add Other Data Type" affordance. Defaults to true for backward compatibility. */
    allowAddNew?: boolean;
    /**
     * When false, renders a plain button trigger (no text input / no typing) and a compact
     * auto-height dropdown panel. Use for fixed short-option lists like Sign Type.
     * Defaults to true for backward compatibility with Magic Upload's Data Type select.
     */
    searchable?: boolean;
    addNewText?: string;
    otherOptionPlaceholderText?: string;
    /**
     * When true, renders the empty/placeholder trigger with neutral grey styling instead of
     * the Magic Upload salmon/pink. Default false — existing callers (Magic Upload) are unaffected.
     */
    neutralPlaceholder?: boolean;
    /**
     * When true, renders the open panel via a React portal on document.body (position: fixed,
     * coordinates from the trigger's getBoundingClientRect). Closes immediately on any scroll
     * outside the panel itself.
     * Use when the trigger lives inside a sticky-positioned cell to escape its stacking context.
     * Default false — Magic Upload and all other existing callers are unaffected.
     */
    portalPanel?: boolean;
    /**
     * When provided, renders a × clear button inside the pill (right-aligned) instead of outside.
     * Only shown when there is a selectedOption. Default undefined — existing callers unaffected.
     */
    onClear?: () => void;
}

const PANEL_MAX_HEIGHT_PX = 17 * 16; // 17rem

const CircularSelect: React.FC<CircularSelectProps> = ({
    option,
    onSelect,
    isFinalized,
    selectedOption,
    placeholder,
    inputStyles,
    selectClassName,
    disabled,
    isSelectingDocType,
    allowAddNew = true,
    searchable = true,
    addNewText = 'Add Other Data Type',
    otherOptionPlaceholderText = 'Enter Data Type...',
    neutralPlaceholder = false,
    portalPanel = false,
    onClear,
}) => {
    const [options, setOptions] = useState<string[]>(option);
    const [filterPhrase, setFilterPhrase] = useState<string | null>(null);
    const [showOptions, setShowOptions] = useState(false);
    const [otherOption, setOtherOption] = useState('');
    const [showOtherOption, setShowOtherOption] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [dontTriggerBlur, setDontTriggerBlur] = useState(false);
    const inputFocus = useRef<HTMLInputElement>(null);
    const [isSearchingDocType, setIsSearchingDocType] = useState(false);

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !(dropdownRef.current as any).contains(event.target)) {
            setOptions(option);
            setShowOptions(false);
        }
    };

    const getIcon = (src) => {
        if (isSelectingDocType) {
            return (
                <LoadingSpinner
                    color="#DF666A"
                    style={{
                        width: '1rem',
                        height: '1rem',
                        position: 'absolute',
                        right: '-2px',
                        top: '0px',
                        zIndex: 10,
                    }}
                />
            );
        }
        return <img src={src} alt="Cross" height={9} width={9} />;
    };

    const renderIcon = () => {
        switch (true) {
            case !!onClear && !!selectedOption && !showOtherOption:
                return (
                    <div
                        className={styles.crossIcon}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onClear!();
                        }}
                        aria-label="Clear"
                        role="button"
                    >
                        {getIcon(PURPLE_CROSS)}
                    </div>
                );
            case showOtherOption:
                return (
                    <div
                        className={styles.crossIcon}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            setShowOtherOption(false);
                            setOtherOption('');
                        }}
                    >
                        {getIcon(PURPLE_CROSS)}
                    </div>
                );
            case !disabled && !!selectedOption:
                return (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!showOtherOption) {
                                setShowOptions((prev) => !prev);
                            }
                        }}
                        className={styles.crossIcon}
                    >
                        {getIcon(PURPLE_DROPDOWN_CARET)}
                    </div>
                );
            case disabled:
                return null;
            case isSearchingDocType || isSelectingDocType:
                return (
                    <LoadingSpinner
                        color="#DF666A"
                        style={{
                            width: '1rem',
                            height: '1rem',
                            position: 'absolute',
                            right: '11px',
                            top: '12px',
                            zIndex: 10,
                        }}
                    />
                );
            default:
                return (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!showOtherOption) {
                                setShowOptions((prev) => !prev);
                            }
                        }}
                        className={neutralPlaceholder && !selectedOption && !showOtherOption ? styles.neutralCaret : styles.arrowIcon}
                    />
                );
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Close portal panel on any scroll outside the panel itself.
    useEffect(() => {
        if (!portalPanel || !showOptions) return;
        const close = (e: Event) => {
            if (e.target instanceof Node && panelRef.current?.contains(e.target)) return;
            setShowOptions(false);
        };
        window.addEventListener('scroll', close, { capture: true });
        return () => window.removeEventListener('scroll', close, { capture: true });
    }, [portalPanel, showOptions]);

    useEffect(() => {
        if (showOtherOption) {
            inputFocus.current?.click();
        }
    }, [showOtherOption]);

    const handleChange = (value: string) => {
        if (!showOtherOption) {
            setOptions(option.filter((item) => item.toLowerCase().includes(value.toLowerCase())));
            setFilterPhrase(value);
            if (!showOptions) setShowOptions(true);
            setIsSearchingDocType(value.length >= 1);
        } else {
            setOtherOption(value);
        }
    };

    useEffect(() => {
        if (showOptions && filterPhrase) {
            setIsSearchingDocType(false);
        }
    }, [options, filterPhrase]);

    const handleSelect = async (option: string) => {
        setLoading(true);
        await onSelect(option);
        setLoading(false);
        setFilterPhrase(null);
        setShowOptions(false);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && showOtherOption) {
                inputFocus.current?.blur();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showOtherOption]);

    const handleBlur = () => {
        setTimeout(async () => {
            if (dontTriggerBlur) {
                // If this flag is set, don't execute the blur logic.
                setDontTriggerBlur(false); // Reset the flag for future actions.
                return;
            }

            // If the user clicked outside and didn't enter any value, reset the state.
            if (otherOption === '') {
                setShowOtherOption(false);
                setFilterPhrase(null);
                return;
            }

            // If the user entered a value in the "other option" input, proceed with selection.
            if (otherOption && showOtherOption) {
                let res = await onSelect(otherOption);
                if (res) {
                    setShowOtherOption(false);
                    setOtherOption('');
                    setFilterPhrase(null);
                }
            }
        }, 0);
    };

    // Non-searchable (compact) trigger: plain button, no typing allowed
    const renderNonSearchableTrigger = () => (
        <button
            type="button"
            style={inputStyles as React.CSSProperties}
            disabled={disabled || isFinalized}
            className={clsx(selectClassName, {
                [styles.selectBox]: true,
                [styles.selectedBox]: !!selectedOption,
                [styles.finalized]: isFinalized || disabled,
            })}
            onClick={() => {
                if (loading) return;
                setShowOptions((prev) => !prev);
            }}
        >
            <span className={styles.selectedText}>{selectedOption ?? placeholder ?? 'Select...'}</span>
        </button>
    );

    // Compute portal panel position inline from the trigger rect (no state, no extra render).
    const portalStyle = (): React.CSSProperties | undefined => {
        if (!portalPanel || !dropdownRef.current) return undefined;
        const rect = dropdownRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < PANEL_MAX_HEIGHT_PX && rect.top > PANEL_MAX_HEIGHT_PX) {
            // Flip up: anchor to the trigger's top so the panel grows upward by its ACTUAL height.
            return {
                position: 'fixed',
                top: 'auto',
                bottom: window.innerHeight - rect.top,
                left: rect.left,
                width: rect.width,
                zIndex: 3000,
            };
        }
        return { position: 'fixed', top: rect.bottom, left: rect.left, width: rect.width, zIndex: 3000 };
    };

    const optionPanel =
        showOptions && !disabled ? (
            <div ref={panelRef} className={searchable ? styles.dropdown : styles.dropdownCompact} style={portalStyle()}>
                {options?.map((option, index) => (
                    <div
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            handleSelect(option);
                        }}
                        key={index}
                        className={styles.option}
                    >
                        {option}
                    </div>
                ))}
                {options.length === 0 && !allowAddNew && <div className={styles.noMatches}>No matches</div>}
                {allowAddNew && (
                    <div
                        onMouseDown={() => {
                            setShowOptions(false);
                            setShowOtherOption(true);
                            setOtherOption(filterPhrase ?? '');
                            setFilterPhrase('');
                            setDontTriggerBlur(true);
                            setTimeout(() => {
                                inputFocus.current?.focus();
                            }, 0);
                        }}
                        className={clsx(styles.option, styles.addNewOption)}
                    >
                        <img src={plusPurpleCircle} alt={'Add'} height={12} width={12} />
                        {addNewText}
                    </div>
                )}
            </div>
        ) : null;

    return (
        <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
            {searchable ? (
                <>
                    <input
                        ref={inputFocus}
                        type="text"
                        style={inputStyles}
                        autoComplete={'off'}
                        placeholder={showOtherOption ? otherOptionPlaceholderText ?? 'Enter Data Type...' : placeholder ?? 'Select...'}
                        value={showOtherOption ? otherOption : filterPhrase !== null ? filterPhrase : selectedOption || otherOption}
                        disabled={disabled}
                        className={clsx(selectClassName, {
                            [styles.selectBox]: true,
                            [styles.neutralEmpty]: neutralPlaceholder && !selectedOption && !showOtherOption,
                            [styles.FilteredOptionAvailableInput]: filterPhrase !== null,
                            [styles.Input]: showOtherOption,
                            [styles.selectedBox]: selectedOption,
                            [styles.finalized]: isFinalized || disabled,
                        })}
                        onClick={(e) => {
                            if (showOtherOption || loading) return;
                            const opening = !showOptions;
                            setShowOptions((prev) => !prev);
                            if (opening) e.currentTarget.select();
                        }}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={handleBlur}
                    />
                    {renderIcon()}
                </>
            ) : (
                <>
                    {renderNonSearchableTrigger()}
                    {renderIcon()}
                </>
            )}

            {portalPanel ? optionPanel && createPortal(optionPanel, document.body) : optionPanel}
        </div>
    );
};

export default CircularSelect;
